import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Card } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, X, Save, Bell, ListChecks, Building2, CalendarOff, History } from "lucide-react";
import TeamMembersPage from "@/pages/Team";
import { toast } from "sonner";

function ListEditor({ label, icon: Icon, items, onChange, testid }) {
  const [val, setVal] = useState("");
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-4 w-4 text-[#C9A24B]" />
        <span className="text-sm text-slate-200">{label}</span>
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {items.map((item, i) => (
          <Badge key={i} data-testid={`${testid}-item`} className="bg-[#0F172A] border border-white/5 text-slate-200 font-normal py-1.5 pl-3 pr-1 gap-1">
            {item}
            <button onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="hover:text-[#f87171] p-0.5">
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input data-testid={`${testid}-input`} value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && val.trim()) { onChange([...items, val.trim()]); setVal(""); } }}
          placeholder="Add new…" className="bg-[#0F172A] border-white/10 text-white" />
        <Button data-testid={`${testid}-add`} onClick={() => { if (val.trim()) { onChange([...items, val.trim()]); setVal(""); } }}
          className="bg-[#1E293B] border border-white/10 hover:bg-[#0F172A]">
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
}

export default function Settings() {
  const { loadMeta } = useApp();
  const [settings, setSettings] = useState(null);
  const [audit, setAudit] = useState([]);

  const load = useCallback(async () => {
    const res = await api.get("/settings");
    setSettings(res.data);
    try { const a = await api.get("/audit", { params: { limit: 40 } }); setAudit(a.data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = (patch) => setSettings((s) => ({ ...s, ...patch }));

  const save = async () => {
    try {
      await api.put("/settings", settings);
      toast.success("Settings saved");
      loadMeta();
    } catch { toast.error("Save failed"); }
  };

  if (!settings) return <div className="text-slate-400">Loading…</div>;

  return (
    <div>
      <PageHeader title="Settings" subtitle="Customise branches, journey stages, follow-up protocols and notification thresholds — no code needed.">
        <Button data-testid="settings-save" onClick={save} className="bg-[#C9A24B] text-[#0F172A] hover:bg-[#b8923f]">
          <Save className="h-4 w-4 mr-2" /> Save All
        </Button>
      </PageHeader>

      <div className="mb-10">
        <TeamMembersPage />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <ListEditor label="Branches" icon={Building2} items={settings.branches || []} onChange={(v) => update({ branches: v })} testid="branches" />
        <ListEditor label="Journey Stages" icon={ListChecks} items={settings.stages || []} onChange={(v) => update({ stages: v })} testid="stages" />
        <ListEditor label="Follow-up Protocol" icon={ListChecks} items={settings.followup_protocol || []} onChange={(v) => update({ followup_protocol: v })} testid="protocol" />

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <CalendarOff className="h-4 w-4 text-[#C9A24B]" />
            <span className="text-sm text-slate-200">Working Days</span>
          </div>
          <Label className="text-slate-300 text-sm">Working days per month (for goal auto-divide)</Label>
          <Input data-testid="working-days" type="number" min={1} max={31} value={settings.working_days_per_month ?? 26}
            onChange={(e) => update({ working_days_per_month: Number(e.target.value) })}
            className="mt-1.5 mb-4 bg-[#0F172A] border-white/10 text-white font-mono-ync" />
          <Label className="text-slate-300 text-sm mb-1.5 block">Weekly Off (optional — rotational? leave empty)</Label>
          <div className="flex flex-wrap gap-1.5">
            {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d) => {
              const on = (settings.weekly_offs || []).includes(d);
              return (
                <button key={d} data-testid={`weekoff-${d}`} onClick={() => update({ weekly_offs: on ? settings.weekly_offs.filter((x) => x !== d) : [...(settings.weekly_offs || []), d] })}
                  className={`px-2.5 py-1 rounded-md text-xs ${on ? "bg-[#C9A24B] text-[#0F172A]" : "bg-[#0F172A] border border-white/10 text-slate-300"}`}>{d.slice(0, 3)}</button>
              );
            })}
          </div>
        </Card>

        <ListEditor label="Holidays (YYYY-MM-DD)" icon={CalendarOff} items={settings.holidays || []} onChange={(v) => update({ holidays: v })} testid="holidays" />

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="h-4 w-4 text-[#C9A24B]" />
            <span className="text-sm text-slate-200">Notification Thresholds</span>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300 text-sm">Alert if a Critical concern is open more than (hrs)</Label>
              <Input data-testid="critical-hours" type="number" value={settings.critical_alert_hours}
                onChange={(e) => update({ critical_alert_hours: Number(e.target.value) })}
                className="mt-1.5 bg-[#0F172A] border-white/10 text-white font-mono-ync" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Alert if a High concern is open more than (hrs)</Label>
              <Input data-testid="high-hours" type="number" value={settings.high_alert_hours}
                onChange={(e) => update({ high_alert_hours: Number(e.target.value) })}
                className="mt-1.5 bg-[#0F172A] border-white/10 text-white font-mono-ync" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Alert if a due is not contacted in (days)</Label>
              <Input data-testid="dues-days" type="number" value={settings.dues_contact_days}
                onChange={(e) => update({ dues_contact_days: Number(e.target.value) })}
                className="mt-1.5 bg-[#0F172A] border-white/10 text-white font-mono-ync" />
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <History className="h-4 w-4 text-[#C9A24B]" />
          <span className="text-sm text-slate-200">Audit Log — recent edits (old → new)</span>
        </div>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {audit.length === 0 && <p className="text-slate-500 text-sm">No edits recorded yet.</p>}
          {audit.map((a) => (
            <div key={a.id} data-testid="audit-row" className="text-xs text-slate-400 border-b border-white/5 pb-2">
              <span className="text-slate-300">{a.by}</span> edited <span className="text-[#C9A24B]">{a.collection}</span> {a.item_name && `(${a.item_name})`} · <span className="text-slate-500">{a.at?.slice(0, 16).replace("T", " ")}</span>
              <div className="mt-0.5">{a.changes?.slice(0, 4).map((c, i) => (
                <span key={i} className="mr-3">{c.field}: <span className="text-[#f87171]">{String(c.old)}</span>→<span className="text-[#34d399]">{String(c.new)}</span></span>
              ))}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
